import React, { useEffect, useState } from "react";
import {useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const Contacts = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get("type") || "";
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(defaultType);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState({ name: "", type: defaultType || "customer", email: "", phone: "", address: "", tin: "", credit_limit: "", credit_terms: 30 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContacts(); }, [filterType]);

  const fetchContacts = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listContacts({ type: filterType || undefined });
    if (res.status === "OK") setContacts(res.details || []);
    else toast.error(res.message);
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name || !form.type) { toast.error("Name and type are required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.createContact(form);
    if (res.status === "OK") {
      toast.success("Contact created!");
      fetchContacts();
      setShowModal(false);
      setForm({ name: "", type: filterType || "customer", email: "", phone: "", address: "", tin: "", credit_limit: "", credit_terms: 30 });
    } else toast.error(res.message);
    setSaving(false);
  };

  const openEdit = (row) => setEditModal({
    id: row.id, name: row.name || "", type: row.type || "customer", email: row.email || "",
    phone: row.phone || "", address: row.address || "", tin: row.tin || "",
    credit_limit: row.credit_limit || "", credit_terms: row.credit_terms || "",
  });

  const handleUpdate = async () => {
    if (!editModal.name) { toast.error("Name is required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.updateContact(editModal);
    if (res.status === "OK") { toast.success("Contact updated!"); fetchContacts(); setEditModal(null); }
    else toast.error(res.message);
    setSaving(false);
  };

  const typeBadge = (type) => {
    const map = { customer: "success", supplier: "primary", both: "warning" };
    return <span className={`badge bg-${map[type] || "secondary"} text-capitalize`}>{type}</span>;
  };

  const columns = [
    { key: "name", label: "Name", render: (v) => <strong>{v}</strong> },
    { key: "type", label: "Type", render: (v) => typeBadge(v) },
    { key: "email", label: "Email", render: (v) => v || "-" },
    { key: "phone", label: "Phone", render: (v) => v || "-" },
    { key: "credit_limit", label: "Credit Limit", render: (v) => v ? <span className="tx-primary">{(+v).toLocaleString()}</span> : "-" },
    { key: "credit_terms", label: "Terms", render: (v) => v ? `${v} days` : "-" },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/contacts/${id}/statement`)} title="Statement"><i className="fa-solid fa-file-lines"></i></button>
          <button className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => openEdit(row)}><i className="fa-solid fa-pen"></i></button>
        </div>
      ),
    },
  ];

  const typeOptions = [{ value: "", label: "All Contacts" }, { value: "customer", label: "Customers" }, { value: "supplier", label: "Suppliers" }, { value: "both", label: "Both" }];

  return (
    <AppContainer
      title="Contacts"
      previousPageTitle="Accounting"
      description="Manage your customers and suppliers. Contacts are used when creating invoices. Each contact can have a credit limit and payment terms set."
      actions={[
        { label: "New Contact", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowModal(true) },
      ]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Filter by Type</label>
            <Select2 options={typeOptions} value={filterType} onChange={setFilterType} />
          </div>
        </div>
      </div>
      <div className="card custom-card p-3">
        <DataTable columns={columns} data={contacts} loading={loading} pageSize={15} emptyMessage="No contacts found." />
      </div>

      {showModal && (
        <AppModal
          title="New Contact"
          size="lg"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Saving..." : "Create Contact"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Full Name / Company <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kampala City Council" />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Type <span className="tx-danger">*</span></label>
              <Select2 options={[{ value: "customer", label: "Customer" }, { value: "supplier", label: "Supplier" }, { value: "both", label: "Both" }]} value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Email</label>
              <input className="form-control" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Phone</label>
              <input className="form-control" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Address</label>
              <textarea className="form-control" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">TIN / Tax ID</label>
              <input className="form-control" value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Credit Limit (UGX)</label>
              <input className="form-control" type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Credit Terms (days)</label>
              <input className="form-control" type="number" value={form.credit_terms} onChange={(e) => setForm({ ...form, credit_terms: e.target.value })} />
            </div>
          </div>
        </AppModal>
      )}
      {editModal && (
        <AppModal
          title="Edit Contact"
          size="lg"
          onClose={() => setEditModal(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Full Name / Company <span className="tx-danger">*</span></label>
              <input className="form-control" value={editModal.name} onChange={(e) => setEditModal({ ...editModal, name: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Type <span className="tx-danger">*</span></label>
              <Select2 options={[{ value: "customer", label: "Customer" }, { value: "supplier", label: "Supplier" }, { value: "both", label: "Both" }]} value={editModal.type} onChange={(v) => setEditModal({ ...editModal, type: v })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Email</label>
              <input className="form-control" type="email" value={editModal.email} onChange={(e) => setEditModal({ ...editModal, email: e.target.value })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Phone</label>
              <input className="form-control" value={editModal.phone} onChange={(e) => setEditModal({ ...editModal, phone: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Address</label>
              <textarea className="form-control" rows={2} value={editModal.address} onChange={(e) => setEditModal({ ...editModal, address: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">TIN / Tax ID</label>
              <input className="form-control" value={editModal.tin} onChange={(e) => setEditModal({ ...editModal, tin: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Credit Limit (UGX)</label>
              <input className="form-control" type="number" value={editModal.credit_limit} onChange={(e) => setEditModal({ ...editModal, credit_limit: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Credit Terms (days)</label>
              <input className="form-control" type="number" value={editModal.credit_terms} onChange={(e) => setEditModal({ ...editModal, credit_terms: e.target.value })} />
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default Contacts;
